/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, NativeModules } from 'react-native';
import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import { useNearestAirportsCodes } from '@airasia-rn/flights/hooks';
import {
  DEFAULT_LANGUAGE,
  QUERY_PERSIST_CACHE,
  useAppConfig,
} from '@airasia-rn/ui';
import { useAccessToken, useUserId } from '@airasia-rn/account/hooks';
import { RecommendationApiService } from '../services/recommendationApiService';
import type {
  MovementsHomeResponse,
  MovementsHomeResponseV4,
  MovementsHomeLocation,
  SearchHistory,
} from '../services/types';
import { useSocialFeedPageContext } from '../context/SocialFeedPageContext';
import { useSearchHistory } from './useSearchHistory';

/**
 * Hook that provides an instance of RecommendationApiService.
 * Uses app config env: stg/dev -> recommend.stg-apiairasia.com, prd -> recommend.apiairasia.com.
 */
export function useRecommendationApiService(): RecommendationApiService {
  const { config } = useAppConfig();
  const environment = config.env ?? 'stg';
  return useMemo(
    () => new RecommendationApiService(environment),
    [environment],
  );
}

export const socialKeys = {
  feed: (
    locale: string,
    market: string,
    limits?: {
      maxPostsPerSection?: number | null;
      maxSections?: number | null;
      maxChips?: number | null;
    },
  ) =>
    [
      QUERY_PERSIST_CACHE,
      'social',
      'feed',
      locale,
      market,
      limits?.maxPostsPerSection,
      limits?.maxSections,
      limits?.maxChips,
    ] as const,
  // NOTE: nothing location-derived (country/lat/lon) may live in this key.
  // Location comes from an async nearest-airport lookup: on first load the
  // country starts as the config.geoId fallback and then flips when the lookup
  // resolves. If it is part of the key, the key changes MID-FLIGHT — the first
  // request's 200 lands on the abandoned key while every observer has moved to
  // the new key and refetches, so the skeleton keeps shimmering even though a
  // response was logged. Keying on locale/market only keeps the key stable for
  // the whole session; the full location still travels in the request body.
  feedV4: (locale: string, market: string) =>
    [QUERY_PERSIST_CACHE, 'social', 'feed', 'v4', locale, market] as const,
  /**
   * Prefix key for invalidating all social feed queries.
   * Use with queryClient.invalidateQueries() to refresh all social feed data
   * (e.g. homepage pull-to-refresh).
   */
  feedPrefix: () => [QUERY_PERSIST_CACHE, 'social', 'feed'] as const,
};

const PAGE_SIZE = 30;

const VOLATILE_SEARCH_HISTORY_KEYS = new Set(['createdAt', 'created_at']);

const buildSearchHistorySignature = (searchHistory: SearchHistory) =>
  JSON.stringify(searchHistory, (key, value) =>
    VOLATILE_SEARCH_HISTORY_KEYS.has(key) ? undefined : value,
  );

// Keyed by the feed's own query key so the v1 and v4 feeds — which are
// separate queries — never claim each other's baseline. Module scope rather
// than context because the baseline has to outlive the feed unmounting; the
// SocialFeedPageContext provider is rendered inside the feed widget itself
// and dies with it.
const lastHandledSearchHistorySignatures = new Map<string, string>();

export const resetSearchHistorySignatureForTests = () => {
  lastHandledSearchHistorySignatures.clear();
};

const useRefetchOnNewSearchHistory = (
  feedScope: readonly unknown[],
  searchHistory: SearchHistory | undefined,
  refetch: () => void,
  hasInitialFetchSettled: boolean,
) => {
  const hasInitialFetchSettledRef = useRef(hasInitialFetchSettled);
  hasInitialFetchSettledRef.current = hasInitialFetchSettled;

  const scopeKey = useMemo(() => JSON.stringify(feedScope), [feedScope]);

  const signature = useMemo(
    () =>
      searchHistory === undefined
        ? undefined
        : buildSearchHistorySignature(searchHistory),
    [searchHistory],
  );

  useEffect(() => {
    if (signature === undefined) return;

    const lastHandled = lastHandledSearchHistorySignatures.get(scopeKey);

    if (lastHandled === undefined) {
      lastHandledSearchHistorySignatures.set(scopeKey, signature);
      return;
    }

    if (lastHandled === signature) return;
    lastHandledSearchHistorySignatures.set(scopeKey, signature);

    if (hasInitialFetchSettledRef.current) {
      refetch();
    }
  }, [scopeKey, signature, refetch]);
};

// How long to wait for optional enrichment (search history, nearest airport)
// before firing the movements request anyway. Enrichment is for personalization
// only; the feed loads immediately even without it. The deadline is short to
// avoid blocking the UI if enrichment queries hang.
const ENRICHMENT_FALLBACK_MS = 250;

const socialFeedInfiniteQueryOptions = (
  apiService: RecommendationApiService,
  getToken: () => Promise<string | undefined>,
  userId: string,
  locale: string,
  market: string,
  currency: string,
  appVersion: string,
  channel: string,
  recommendationHashKey: string,
  location?: MovementsHomeLocation,
  searchHistory?: SearchHistory,
) => {
  return infiniteQueryOptions<MovementsHomeResponseV4>({
    queryKey: socialKeys.feedV4(locale, market),
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1, signal }) => {
      const token = await getToken().catch(() => '');
      const accessToken = token || '';
      const response = await apiService.fetchMovementsHomeV4({
        accessToken,
        locale,
        market,
        platform: Platform.OS,
        channel,
        channelHash: recommendationHashKey,
        appVersion,
        userId,
        currency,
        searchHistory,
        location,
        page: pageParam as number,
        pageSize: PAGE_SIZE,
        signal,
      });

      return response;
    },
    getNextPageParam: (lastPage, allPages) => {
      // The API returns `totalRecords` and `pageSize`. We can calculate if we have more pages.
      // Use totalRecords from first page as fallback if not present on subsequent pages.
      const totalRecords =
        lastPage.totalRecords ?? allPages[0]?.totalRecords ?? 0;
      const maxPages = Math.ceil(
        totalRecords / (lastPage.pageSize || PAGE_SIZE),
      );
      const currentPage = lastPage.page ?? allPages.length;

      if (currentPage < maxPages) {
        return currentPage + 1;
      }
      return undefined;
    },
    // Cache for 5 minutes so the 3 feed observers share one request
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 3,
  });
};

const legacySocialFeedInfiniteQueryOptions = (
  apiService: RecommendationApiService,
  getToken: () => Promise<string | undefined>,
  userId: string,
  locale: string,
  market: string,
  currency: string,
  appVersion: string,
  channel: string,
  recommendationHashKey: string,
  maxPostsPerSection: number | null | undefined,
  maxSections: number | null | undefined,
  maxChips: number | null | undefined,
  location?: MovementsHomeLocation,
  searchHistory?: SearchHistory,
) => {
  return infiniteQueryOptions<MovementsHomeResponse>({
    queryKey: socialKeys.feed(locale, market, {
      maxPostsPerSection,
      maxSections,
      maxChips,
    }),
    initialPageParam: 1,
    queryFn: async () => {
      const token = await getToken().catch(() => '');
      const accessToken = token || '';
      const response = await apiService.fetchMovementsHome({
        accessToken,
        locale,
        market,
        platform: Platform.OS,
        channelHash: recommendationHashKey,
        appVersion,
        maxPostsPerSection,
        maxSections,
        maxChips,
        searchHistory,
        location,
      });

      return response;
    },
    getNextPageParam: () => {
      return undefined;
    },
    staleTime: 30000,
    refetchOnWindowFocus: false,
    retry: 3,
  });
};

export const useSocialFeedLegacy = () => {
  const { data: searchHistory, isPending: isSearchHistoryPending } =
    useSearchHistory();
  const apiService = useRecommendationApiService();
  const { config } = useAppConfig();
  const channel = process.env.EXPO_PUBLIC_RECOMMENDATION_CHANNEL ?? '';
  const {
    recommendationHashKey: contextHash,
    maxPostsPerSection,
    maxSections,
    maxChips,
  } = useSocialFeedPageContext();
  const recommendationHashKey =
    contextHash || process.env.EXPO_PUBLIC_RECOMMENDATION_HASH_KEY || '';
  const locale = config.locale ?? DEFAULT_LANGUAGE;
  const market = config.geoId ?? 'MY';
  const appVersion = config.appVersion ?? '';

  const accessToken = useAccessToken('social');
  const userIdQuery = useUserId('social');
  const nearestAirportQuery = useNearestAirportsCodes();

  const nearestAirportLatLong = nearestAirportQuery.data?.latlong ?? '';
  const location = useMemo(() => {
    const [lat, lon] = nearestAirportLatLong
      .split(',')
      .map((value) => Number(value.trim()));

    return {
      country: nearestAirportQuery.data?.country || config.geoId || 'MY',
      city: nearestAirportQuery.data?.city ?? '',
      nearestAirport: nearestAirportQuery.data?.airport?.[0] ?? '',
      latitude: Number.isFinite(lat) ? lat : 0,
      longitude: Number.isFinite(lon) ? lon : 0,
    };
  }, [
    config.geoId,
    nearestAirportQuery.data?.airport,
    nearestAirportQuery.data?.city,
    nearestAirportQuery.data?.country,
    nearestAirportLatLong,
  ]);

  const [enrichmentDeadlineReached, setEnrichmentDeadlineReached] =
    useState(false);
  useEffect(() => {
    const timer = setTimeout(
      () => setEnrichmentDeadlineReached(true),
      ENRICHMENT_FALLBACK_MS,
    );
    return () => clearTimeout(timer);
  }, []);

  const enrichmentReady =
    !isSearchHistoryPending && !nearestAirportQuery.isPending;

  const query = useInfiniteQuery({
    ...legacySocialFeedInfiniteQueryOptions(
      apiService,
      accessToken.getToken,
      userIdQuery.data || '',
      locale,
      market,
      config.currency || 'MYR',
      appVersion,
      channel,
      recommendationHashKey,
      maxPostsPerSection,
      maxSections,
      maxChips,
      location,
      searchHistory ?? undefined,
    ),
    enabled:
      !!accessToken.data &&
      accessToken.isTokenValid &&
      !userIdQuery.isLoading &&
      (enrichmentReady || enrichmentDeadlineReached),
  });

  useRefetchOnNewSearchHistory(
    socialKeys.feed(locale, market, {
      maxPostsPerSection,
      maxSections,
      maxChips,
    }),
    searchHistory,
    query.refetch,
    !query.isPending,
  );

  return query;
};

export const useSocialFeed = (options?: { enabled?: boolean }) => {
  const isCallerEnabled = options?.enabled ?? true;
  const { data: searchHistory, isPending: isSearchHistoryPending } =
    useSearchHistory();
  const apiService = useRecommendationApiService();
  const { config } = useAppConfig();
  const channel = process.env.EXPO_PUBLIC_RECOMMENDATION_CHANNEL ?? '';
  const { recommendationHashKey: contextHash } = useSocialFeedPageContext();
  const recommendationHashKey =
    contextHash || process.env.EXPO_PUBLIC_RECOMMENDATION_HASH_KEY || '';
  const locale = config.locale ?? DEFAULT_LANGUAGE;
  const market = config.geoId ?? 'MY';
  const appVersion = config.appVersion ?? '';

  const accessToken = useAccessToken('social');
  const userIdQuery = useUserId('social');
  const nearestAirportQuery = useNearestAirportsCodes();

  const nearestAirportLatLong = nearestAirportQuery.data?.latlong ?? '';
  const location = useMemo(() => {
    const [lat, lon] = nearestAirportLatLong
      .split(',')
      .map((value) => Number(value.trim()));

    return {
      country: nearestAirportQuery.data?.country || config.geoId || 'MY',
      city: nearestAirportQuery.data?.city ?? '',
      nearestAirport: nearestAirportQuery.data?.airport?.[0] ?? '',
      latitude: Number.isFinite(lat) ? lat : 0,
      longitude: Number.isFinite(lon) ? lon : 0,
    };
  }, [
    config.geoId,
    nearestAirportQuery.data?.airport,
    nearestAirportQuery.data?.city,
    nearestAirportQuery.data?.country,
    nearestAirportLatLong,
  ]);

  // Fallback deadline: fire the request even if optional enrichment never
  // settles, so a hung dependency can't deadlock the feed into an endless
  // skeleton with no request ever sent.
  const [enrichmentDeadlineReached, setEnrichmentDeadlineReached] =
    useState(false);
  useEffect(() => {
    const timer = setTimeout(
      () => setEnrichmentDeadlineReached(true),
      ENRICHMENT_FALLBACK_MS,
    );
    return () => clearTimeout(timer);
  }, []);

  const enrichmentReady =
    !isSearchHistoryPending && !nearestAirportQuery.isPending;

  const query = useInfiniteQuery({
    ...socialFeedInfiniteQueryOptions(
      apiService,
      accessToken.getToken,
      userIdQuery.data || '',
      locale,
      market,
      config.currency || 'MYR',
      appVersion,
      channel,
      recommendationHashKey,
      location,
      searchHistory ?? undefined,
    ),
    // Movements is a signed-in feature, so authentication still gates the
    // request. Search history and nearest-airport are optional enrichment that
    // travel in the request body: wait for them when they resolve quickly, but
    // never let one hanging dependency block the fetch forever — the deadline
    // releases the gate so the feed always loads. `isCallerEnabled` lets a
    // caller that only conditionally renders the v4 feed (e.g. behind a
    // layout flag) opt out of firing the request entirely, so this hook
    // doesn't race the legacy `useSocialFeedLegacy()` v1 request.
    enabled:
      isCallerEnabled &&
      !!accessToken.data &&
      accessToken.isTokenValid &&
      !userIdQuery.isLoading &&
      (enrichmentReady || enrichmentDeadlineReached),
  });

  useRefetchOnNewSearchHistory(
    socialKeys.feedV4(locale, market),
    searchHistory,
    query.refetch,
    !query.isPending,
  );

  return query;
};
